(() => {
  const SIZE_CHARTS = {
    'snap-on': [
      { size: 'XXS', min: 20.32, max: 27.94, displayMin: 20, displayMax: 28 },
      { size: 'XS', min: 25.4, max: 35.56, displayMin: 25, displayMax: 36 },
      { size: 'S', min: 30.48, max: 40.64, displayMin: 30, displayMax: 41 },
      { size: 'M', min: 35.56, max: 45.72, displayMin: 36, displayMax: 46 },
      { size: 'L', min: 40.64, max: 50.8, displayMin: 41, displayMax: 51 },
      { size: 'XL', min: 45.72, max: 55.88, displayMin: 46, displayMax: 56 },
      { size: 'XXL', min: 50.8, max: 60.96, displayMin: 51, displayMax: 61 }
    ],
    'tie-on': [
      { size: 'XS', min: 17.9, max: 25.4, displayMin: 18, displayMax: 25 },
      { size: 'S', min: 22.8, max: 33.02, displayMin: 23, displayMax: 33 },
      { size: 'M', min: 30.48, max: 43, displayMin: 30, displayMax: 43 },
      { size: 'L', min: 38.1, max: 50.8, displayMin: 38, displayMax: 51 },
      { size: 'XL', min: 48.26, max: 63.5, displayMin: 48, displayMax: 64 }
    ],
    'over-collar': [
      { size: 'XS', max: 30 },
      { size: 'S', max: 37 },
      { size: 'M', max: 44 },
      { size: 'L', max: 56 },
      { size: 'XL', max: 64 }
    ]
  };

  const toCm = (value, unit) => unit === 'in' ? value * 2.54 : value;

  const chooseSize = (style, cm) => {
    const chart = SIZE_CHARTS[style] || [];
    if (style === 'over-collar') return chart.find((entry) => cm <= entry.max) || null;
    const matches = chart.filter((entry) => cm >= entry.min && cm <= entry.max);
    return matches.length ? matches[matches.length - 1] : null;
  };

  const getProductInfo = (root) => root.closest('product-info');

  const clearBreedRecommendation = (root) => {
    const productInfo = getProductInfo(root);
    const breedRoot = productInfo?.querySelector('[data-hughies-breed-selector]');
    if (!breedRoot) return;
    const select = breedRoot.querySelector('[data-breed-select]');
    const result = breedRoot.querySelector('[data-results]');
    const error = breedRoot.querySelector('[data-error]');
    if (select) select.value = '';
    if (result) {
      result.hidden = true;
      result.textContent = '';
    }
    if (error) error.hidden = true;
  };

  const getSizeControl = (root, size) => {
    const variantSelects = getProductInfo(root)?.querySelector('variant-selects, variant-radios');
    if (!variantSelects) return null;
    const normalized = String(size).trim().toLowerCase();
    const sizeFieldset = Array.from(variantSelects.querySelectorAll('fieldset')).find((fieldset) =>
      String(fieldset.querySelector('legend')?.textContent || '').trim().toLowerCase().startsWith('size')
    );
    const radio = sizeFieldset && Array.from(sizeFieldset.querySelectorAll('input')).find((item) =>
      String(item.value || '').trim().toLowerCase() === normalized
    );
    if (radio) return { type: 'input', element: radio };

    const select = Array.from(variantSelects.querySelectorAll('select')).find((item) => {
      const name = String(item.name || '').trim().toLowerCase();
      return name === 'options[size]' || name.includes('[size]');
    });
    const option = select && Array.from(select.options).find((item) =>
      String(item.value || '').trim().toLowerCase() === normalized
    );
    return option ? { type: 'select', element: select, value: option.value } : null;
  };

  const applySize = (root, size) => {
    const control = getSizeControl(root, size);
    const productInfo = getProductInfo(root);
    if (!control || !productInfo) return false;

    productInfo.dataset.hughiesApplyingSize = 'measurement';
    if (control.type === 'input') {
      if (!control.element.checked) control.element.click();
    } else if (control.element.value !== control.value) {
      control.element.value = control.value;
      control.element.dispatchEvent(new Event('input', { bubbles: true }));
      control.element.dispatchEvent(new Event('change', { bubbles: true }));
    }
    window.setTimeout(() => delete productInfo.dataset.hughiesApplyingSize, 0);
    return control.type === 'input' ? control.element.checked : control.element.value === control.value;
  };

  const guideNote = (style) => style === 'over-collar'
    ? 'We choose the smallest size that fits. If your collar is close to the upper limit, choose the next size up.'
    : 'If a measurement fits more than one size, we choose the larger size.';

  const ensureInlineGuide = (root) => {
    if (root.querySelector('[data-bandana-size-guide-inline]')) return;
    const style = root.dataset.bandanaStyle || '';
    const chart = SIZE_CHARTS[style];
    const anchor = root.querySelector('#byb-neck-help') || root.querySelector('.byb-size');
    if (!chart || !anchor) return;

    const details = document.createElement('details');
    details.setAttribute('data-bandana-size-guide-inline', '');
    details.style.cssText = 'margin:0.8rem 0 0;padding:0.85rem 1rem;border:1px solid rgba(var(--color-foreground),0.16);border-radius:8px;background:rgba(var(--color-foreground),0.025);';
    const rows = chart.map((entry) => {
      const fit = style === 'over-collar'
        ? `Up to ${entry.max} cm collar length`
        : `${entry.displayMin}–${entry.displayMax} cm`;
      return `<tr><td style="padding:.45rem .35rem;font-weight:700;">${entry.size}</td><td style="padding:.45rem .35rem;">${fit}</td></tr>`;
    }).join('');
    const heading = style === 'over-collar' ? 'Collar length' : 'Neck fit';
    details.innerHTML = `<summary style="cursor:pointer;font-weight:700;">Size guide — view measurements</summary><div style="padding-top:.65rem;"><table style="width:100%;border-collapse:collapse;font-size:.92em;"><thead><tr><th style="text-align:left;padding:.4rem .35rem;border-bottom:1px solid rgba(var(--color-foreground),.15);">Size</th><th style="text-align:left;padding:.4rem .35rem;border-bottom:1px solid rgba(var(--color-foreground),.15);">${heading}</th></tr></thead><tbody>${rows}</tbody></table><p style="margin:.65rem 0 0;font-size:.9em;">${guideNote(style)}</p></div>`;
    anchor.insertAdjacentElement('afterend', details);
  };

  const updateRecommendation = (root) => {
    const input = root.querySelector('[data-neck-size]');
    const unit = root.querySelector('[data-neck-unit]');
    const result = root.querySelector('[data-size-recommendation]');
    const style = root.dataset.bandanaStyle || '';
    if (!input || !result || !SIZE_CHARTS[style]) return;

    const raw = String(input.value || '').trim();
    const value = Number(raw);
    root.dataset.measurementManualOverride = 'false';
    if (!raw || !Number.isFinite(value) || value <= 0) {
      delete root.dataset.measurementInRange;
      result.hidden = true;
      result.textContent = '';
      return;
    }

    clearBreedRecommendation(root);
    const recommendation = chooseSize(style, toCm(value, unit?.value || 'cm'));
    if (!recommendation) {
      root.dataset.measurementInRange = 'false';
      result.textContent = 'No listed size fits this measurement. Please contact me before ordering.';
      result.hidden = false;
      return;
    }

    root.dataset.measurementInRange = 'true';
    const selected = applySize(root, recommendation.size);
    const explanation = style === 'over-collar'
      ? 'We chose the smallest size that fits.'
      : 'If the measurement overlaps sizes, we chose the larger size.';
    result.textContent = selected
      ? `Recommended size: ${recommendation.size} — selected for you. ${explanation}`
      : `Recommended size: ${recommendation.size}. Please select it manually.`;
    result.hidden = false;
  };

  const isSizeControl = (target) => {
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return false;
    const fieldset = target.closest('fieldset');
    if (fieldset && String(fieldset.querySelector('legend')?.textContent || '').trim().toLowerCase().startsWith('size')) return true;
    return String(target.name || '').trim().toLowerCase().includes('[size]');
  };

  const initialiseManualOverride = () => {
    if (window.hughiesBandanaManualSizingReady) return;
    window.hughiesBandanaManualSizingReady = true;
    document.addEventListener('change', (event) => {
      const productInfo = event.target.closest('product-info');
      if (!productInfo) return;
      if (!isSizeControl(event.target) || productInfo.dataset.hughiesApplyingSize) return;
      const build = productInfo.querySelector('build-your-bandana[data-bandana-style]');
      const breedRoot = productInfo.querySelector('[data-hughies-breed-selector]');
      const breedSelect = breedRoot?.querySelector('[data-breed-select]');
      const breedResult = breedRoot?.querySelector('[data-results]');
      const breedError = breedRoot?.querySelector('[data-error]');
      if (breedSelect) breedSelect.value = '';
      if (breedResult) {
        breedResult.hidden = true;
        breedResult.textContent = '';
      }
      if (breedError) breedError.hidden = true;
      if (!build) return;

      const result = build.querySelector('[data-size-recommendation]');
      const measurement = build.querySelector('[data-neck-size]');
      const unit = build.querySelector('[data-neck-unit]')?.value === 'in' ? 'inches' : 'cm';
      const size = String(event.target.value || '').trim();
      if (build.dataset.measurementInRange === 'false') {
        build.dataset.measurementManualOverride = 'false';
        if (result) {
          result.textContent = 'No listed size fits this measurement. Please contact me before ordering.';
          result.hidden = false;
        }
        return;
      }
      build.dataset.measurementManualOverride = 'true';
      if (result && size) {
        const suffix = measurement?.value
          ? ` Your ${measurement.value} ${unit} measurement will be included with the order.`
          : '';
        result.textContent = `Size ${size} selected manually.${suffix}`;
        result.hidden = false;
      }
    });
  };

  const initialise = (root) => {
    if (!root || root.dataset.measurementSizingReady === 'true') return;
    ensureInlineGuide(root);
    initialiseManualOverride(root);
    const input = root.querySelector('[data-neck-size]');
    const unit = root.querySelector('[data-neck-unit]');
    if (!input) return;
    root.dataset.measurementSizingReady = 'true';
    input.addEventListener('input', () => updateRecommendation(root));
    input.addEventListener('change', () => updateRecommendation(root));
    input.addEventListener('blur', () => updateRecommendation(root));
    unit?.addEventListener('change', () => updateRecommendation(root));
  };

  const initialiseAll = () => document.querySelectorAll('build-your-bandana[data-bandana-style]').forEach(initialise);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }
  document.addEventListener('shopify:section:load', initialiseAll);
  document.addEventListener('product-info:loaded', initialiseAll);
})();
